import { useEffect, useState } from "react";
import { RefreshCw, Download, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";
import { PayinOrderDrawer, StatusPill, BUCKET_META } from "@/components/payment/PayinOrderDrawer";
import { CopyBtn } from "@/components/payment/VendorResponse";
import { timeAgo } from "@/components/admin/PayinHealthBar";
import { StuckBanner, StuckChip, CallbackIcon, BulkBar } from "@/components/payment/PayinHistoryExtras";

const TABS = [["all", "All"], ...Object.entries(BUCKET_META).map(([k, v]) => [k, v.label])];

/** Shared PayIn history view. `paths` = {list, csv, verify(id), receipt(id), fullLog?(id)}. */
export function PayinHistory({ paths, isAdmin = false, partners = [], testid = "payin-history" }) {
    const [status, setStatus] = useState("all");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [search, setSearch] = useState("");
    const [q, setQ] = useState("");
    const [partnerId, setPartnerId] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState("");
    const [open, setOpen] = useState(null);
    const [tick, setTick] = useState(0);
    const [stuck, setStuck] = useState(() => new URLSearchParams(window.location.search).get("stuck") === "1");
    const [selected, setSelected] = useState([]);
    const partnerName = Object.fromEntries(partners.map((p) => [p.partner_id, p.business_name]));

    useEffect(() => { const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 350); return () => clearTimeout(t); }, [search]);

    const params = () => {
        const p = new URLSearchParams({ status });
        if (from) p.set("date_from", from);
        if (to) p.set("date_to", to);
        if (q) p.set("q", q);
        if (partnerId) p.set("partner_id", partnerId);
        if (stuck) p.set("stuck", "true");
        return p;
    };

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr("");
        const p = params(); p.set("page", page); p.set("page_size", 25);
        api.get(`${paths.list}?${p}`)
            .then((r) => alive && setData(r.data))
            .catch((e) => alive && setErr(formatDetail(e?.response?.data?.detail) || e.message))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [status, from, to, q, partnerId, page, tick, stuck]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setSelected([]); }, [status, from, to, q, partnerId, page, stuck]);

    const hasInProgress = (data?.items || []).some((o) => o.status_bucket === "in_progress");
    useEffect(() => {
        if (!hasInProgress) return undefined;
        const t = setInterval(() => { if (document.visibilityState === "visible") setTick((x) => x + 1); }, 30000);
        return () => clearInterval(t);
    }, [hasInProgress]);

    const download = async (kind) => {
        setExporting(kind);
        try {
            const r = await api.get(`${paths[kind]}?${params()}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: kind === "pdf" ? "application/pdf" : "text/csv" }));
            const a = document.createElement("a");
            a.href = url; a.download = `payin-${status}-${new Date().toISOString().slice(0, 10)}.${kind}`;
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setExporting(""); }
    };

    const setFilter = (fn) => (v) => { fn(v); setPage(1); };
    const counts = data?.counts || {};
    const s = data?.summary;
    const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

    return (
        <div className="space-y-4" data-testid={testid}>
            <div className="flex flex-wrap items-center justify-end gap-2">
                <AutoCheckedLabel av={data?.auto_verify} live={hasInProgress} testid={testid} />
                <button onClick={() => setTick((t) => t + 1)} data-testid={`${testid}-refresh`}
                        className="btn-secondary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
                {[["csv", "Excel (CSV)"], ["pdf", "PDF"]].map(([k, l]) => (
                    <button key={k} onClick={() => download(k)} disabled={!!exporting} data-testid={`${testid}-download${k === "pdf" ? "-pdf" : ""}`}
                            className={`${k === "pdf" ? "btn-secondary" : "btn-primary"} px-3 py-2 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-60`}>
                        {exporting === k ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {l}
                    </button>
                ))}
            </div>

            <Filters {...{ testid, from, to, search, partnerId, isAdmin, partners }}
                     setFrom={setFilter(setFrom)} setTo={setFilter(setTo)} setSearch={setSearch} setPartnerId={setFilter(setPartnerId)} />

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Stat label="Successful PayIns" value={s?.success_count ?? "—"} sub="captured transactions" testid={`${testid}-stat-success`} />
                <Stat label="Total Collected" value={s ? fmtINR(s.collected_amount) : "—"} sub="gross from customers" tone="slate" testid={`${testid}-stat-collected`} />
                <Stat label="Total Commission" value={s ? fmtINR(s.commission_amount) : "—"} sub="charged on successful PayIns" tone="orange" testid={`${testid}-stat-commission`} />
                <Stat label="Net Received" value={s ? fmtINR(s.net_amount) : "—"} sub="collected − commission" testid={`${testid}-stat-net`} />
                <Stat label="Success Rate" value={s?.success_rate_pct == null ? "—" : `${s.success_rate_pct}%`} sub="of completed orders" tone="slate" testid={`${testid}-stat-rate`} />
            </div>

            <div className="flex gap-1 overflow-x-auto border-b border-slate-200" data-testid={`${testid}-tabs`}>
                {TABS.map(([k, l]) => (
                    <button key={k} onClick={() => { setStatus(k); setPage(1); }} data-testid={`${testid}-tab-${k}`}
                            className={`px-3 py-2 text-xs font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${status === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                        {l}
                        <span data-testid={`${testid}-count-${k}`} className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono ${status === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{counts[k] ?? 0}</span>
                    </button>
                ))}
            </div>

            <StuckBanner count={counts.stuck || 0} minutes={data?.stuck_minutes || 60} active={stuck} testid={testid}
                         onToggle={() => { setStuck((v) => !v); setPage(1); }} />

            {data?.multi && <MultiIdBar m={data.multi} testid={testid} onClear={() => setSearch("")} />}

            {err && <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700" data-testid={`${testid}-error`}>{err}</div>}

            <HistoryTable items={data?.items} isAdmin={isAdmin} partnerName={partnerName} onOpen={setOpen} testid={testid}
                          paths={paths} offset={((data?.page || 1) - 1) * (data?.page_size || 25)} onChanged={() => setTick((t) => t + 1)}
                          selected={selected} setSelected={setSelected} />

            <BulkBar ids={selected} bulkPath={paths.bulkVerify} testid={testid}
                     onDone={() => { setSelected([]); setTick((t) => t + 1); }} onClear={() => setSelected([])} />

            {data && data.total > data.page_size && (
                <div className="flex items-center justify-between text-xs text-slate-500" data-testid={`${testid}-pager`}>
                    <span>Page {data.page} of {pages} · {data.total} orders</span>
                    <div className="flex gap-1">
                        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid={`${testid}-prev`} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
                        <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid={`${testid}-next`} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                </div>
            )}

            {open && <PayinOrderDrawer order={open} api={paths} isAdmin={isAdmin} partnerName={partnerName[open.partner_id]}
                                       onClose={() => setOpen(null)} onUpdated={() => setTick((t) => t + 1)} />}
        </div>
    );
}

function MultiIdBar({ m, testid, onClear }) {
    return (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs" data-testid={`${testid}-multi`}>
            <span className="font-semibold text-slate-800" data-testid={`${testid}-multi-requested`}>{m.requested} IDs</span>
            <span className="text-emerald-700" data-testid={`${testid}-multi-found`}>{m.found} found</span>
            <span className={m.not_found.length ? "text-rose-700 font-semibold" : "text-slate-400"} data-testid={`${testid}-multi-missing`}>{m.not_found.length} not found</span>
            {m.not_found.length > 0 && (
                <span className="flex flex-wrap items-center gap-1 min-w-0" data-testid={`${testid}-multi-missing-list`}>
                    {m.not_found.slice(0, 8).map((id) => <code key={id} className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 text-[10px]">{id}</code>)}
                    {m.not_found.length > 8 && <span className="text-[10px] text-slate-500">+{m.not_found.length - 8} more</span>}
                </span>
            )}
            <span className="ml-auto flex items-center gap-3">
                {m.not_found.length > 0 && <CopyBtn text={m.not_found.join("\n")} label="Copy not found" testid={`${testid}-multi-copy`} />}
                <button onClick={onClear} data-testid={`${testid}-multi-clear`} className="text-[11px] font-medium text-slate-500 hover:text-slate-900">Clear</button>
            </span>
        </div>
    );
}

function AutoCheckedLabel({ av, live, testid }) {
    if (!av) return null;
    const off = av.minutes === 0;
    const every = av.minutes ? `every ${av.minutes} min` : av.minutes_set?.length ? "per-partner interval" : "";
    return (
        <span className="mr-auto inline-flex items-center gap-1.5 text-[11px] text-slate-500" data-testid={`${testid}-autocheck`}
              title={live ? "This page reloads every 30s while orders are in progress" : undefined}>
            <span className={`w-1.5 h-1.5 rounded-full ${off ? "bg-slate-300" : live ? "bg-emerald-500 animate-pulse" : "bg-emerald-500"}`} />
            {off ? "Auto-verify off" : `${av.last_run_at ? `Auto-checked ${timeAgo(av.last_run_at)}` : "Auto-verify on"}${every ? ` · ${every}` : ""}`}
        </span>
    );
}

function Filters({ testid, from, to, search, partnerId, isAdmin, partners, setFrom, setTo, setSearch, setPartnerId }) {
    const L = ({ children }) => <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{children}</div>;
    return (
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
            <div className="flex-1"><L>From</L><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid={`${testid}-from`} className="input text-sm mt-1 w-full" /></div>
            <div className="flex-1"><L>To</L><input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid={`${testid}-to`} className="input text-sm mt-1 w-full" /></div>
            {isAdmin && (
                <div className="flex-1"><L>Partner</L>
                    <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)} data-testid={`${testid}-partner`} className="input text-sm mt-1 w-full py-2">
                        <option value="">All partners</option>
                        {partners.map((p) => <option key={p.partner_id} value={p.partner_id}>{p.business_name}</option>)}
                    </select>
                </div>
            )}
            <div className="flex-[2]"><L>Search</L>
                <textarea value={search} onChange={(e) => setSearch(e.target.value)} data-testid={`${testid}-search`}
                          rows={Math.min(4, Math.max(1, search.split("\n").length))}
                          placeholder="Order ID / ref / UTR / customer — paste many IDs separated by comma, space or new line"
                          className="input text-sm mt-1 w-full resize-none leading-5 py-2 block" />
            </div>
        </div>
    );
}

const inr2 = (n) => (n == null || isNaN(n) ? "—" : "₹" + Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "numeric", minute: "2-digit", hour12: true });

function RowRefresh({ o, paths, onDone, testid }) {
    const [busy, setBusy] = useState(false);
    if (o.status_bucket !== "in_progress") return null;
    const run = async (e) => {
        e.stopPropagation(); setBusy(true);
        try { await api.post(paths.verify(o.order_id)); } catch { /* status unchanged */ }
        finally { setBusy(false); onDone(); }
    };
    return (
        <button onClick={run} disabled={busy} title="Refresh status from vendor" data-testid={testid}
                className="p-1 rounded-full text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors disabled:opacity-60">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
        </button>
    );
}

function HistoryTable({ items, isAdmin, partnerName, onOpen, testid, paths, offset, onChanged, selected, setSelected }) {
    const selectable = (items || []).filter((o) => o.status_bucket === "in_progress").map((o) => o.order_id);
    const allOn = selectable.length > 0 && selectable.every((id) => selected.includes(id));
    const toggle = (id) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= 100 ? cur : [...cur, id]));
    const TH = ({ children, right }) => <th className={`px-3 py-2.5 font-semibold ${right ? "text-right" : "text-left"}`}>{children}</th>;
    return (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden overflow-x-auto">
            {!items ? (
                <p className="text-sm text-slate-400 text-center py-10">Loading…</p>
            ) : items.length === 0 ? (
                <p data-testid={`${testid}-empty`} className="text-sm text-slate-400 text-center py-10">No PayIn transactions match these filters.</p>
            ) : (
                <table className="w-full text-xs min-w-[1040px]">
                    <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                        <tr>
                            <th className="pl-3 py-2.5 w-6">
                                <input type="checkbox" checked={allOn} disabled={!selectable.length} data-testid={`${testid}-select-all`}
                                       title="Select all in progress on this page"
                                       onChange={() => setSelected(allOn ? [] : selectable.slice(0, 100))} className="accent-slate-900 cursor-pointer" />
                            </th>
                            <TH>#</TH><TH>Order</TH><TH>Owner</TH><TH right>Amount</TH><TH right>Commission</TH><TH right>Net</TH>
                            <TH>Status</TH><TH>UTR</TH><TH>Date</TH>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((o, i) => {
                            const est = o.commission_estimated;
                            return (
                            <tr key={o.order_id} onClick={() => onOpen(o)} data-testid={`${testid}-row-${o.order_id}`}
                                className="border-t border-slate-100 cursor-pointer hover:bg-slate-50/80 transition-colors">
                                <td className="pl-3 py-3" onClick={(e) => e.stopPropagation()}>
                                    {o.status_bucket === "in_progress" && (
                                        <input type="checkbox" checked={selected.includes(o.order_id)} onChange={() => toggle(o.order_id)}
                                               data-testid={`${testid}-select-${o.order_id}`} className="accent-slate-900 cursor-pointer" />
                                    )}
                                </td>
                                <td className="px-3 py-3 text-slate-400 font-mono">{offset + i + 1}</td>
                                <td className="px-3 py-3 font-mono">
                                    <div className="text-blue-700 font-medium">{o.order_id}</div>
                                    <div className="text-[10px] text-slate-400">{o.client_reference_id}</div>
                                </td>
                                <td className="px-3 py-3" data-testid={`${testid}-owner-${o.order_id}`}>
                                    {isAdmin && <div className="font-medium text-slate-800">{partnerName[o.partner_id] || o.partner_id}</div>}
                                    <div className={isAdmin ? "text-[10px] text-slate-500" : "font-medium text-slate-800"}>{o.submerchant_label || "Self"}</div>
                                </td>
                                <td className="px-3 py-3 text-right font-mono font-semibold text-slate-900 whitespace-nowrap">{inr2(o.amount)}</td>
                                <td className="px-3 py-3 text-right whitespace-nowrap" data-testid={`${testid}-commission-${o.order_id}`}>
                                    <div className={`font-mono font-semibold ${est ? "text-orange-400" : "text-orange-600"}`}>{inr2(o.commission_amount)}</div>
                                    <div className="text-[10px] text-slate-400">{o.commission_percent != null ? `${+o.commission_percent}%` : ""}{est ? " · est." : ""}</div>
                                </td>
                                <td className={`px-3 py-3 text-right font-mono font-semibold whitespace-nowrap ${est ? "text-emerald-500/70" : "text-emerald-700"}`} data-testid={`${testid}-net-${o.order_id}`}>
                                    {inr2(o.net_amount)}
                                </td>
                                <td className="px-3 py-3">
                                    <div className="flex items-center gap-1">
                                        <StatusPill order={o} testid={`${testid}-status-${o.order_id}`} />
                                        {o.is_stuck && <StuckChip testid={`${testid}-stuck-${o.order_id}`} />}
                                        <CallbackIcon cb={o.last_callback} testid={`${testid}-cb-${o.order_id}`} />
                                        <RowRefresh o={o} paths={paths} onDone={onChanged} testid={`${testid}-row-refresh-${o.order_id}`} />
                                    </div>
                                    {o.status_bucket === "failed" && o.response_message && <div className="text-[10px] text-rose-600 mt-0.5 max-w-[200px] truncate" title={o.response_message}>{o.response_message}</div>}
                                </td>
                                <td className="px-3 py-3 font-mono text-[11px] text-slate-600">{o.utr || "—"}</td>
                                <td className="px-3 py-3 text-slate-500 text-[11px] whitespace-nowrap">{fmtDate(o.created_at)}</td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}

const TONES = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    orange: "bg-orange-50 border-orange-200 text-orange-900",
    slate: "bg-white border-slate-200 text-slate-900",
};

function Stat({ label, value, sub, testid, tone = "emerald" }) {
    return (
        <div data-testid={testid} className={`${TONES[tone]} border rounded-xl p-4`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className="text-xl lg:text-2xl font-bold mt-1 font-mono truncate">{value}</div>
            <div className="text-[10px] text-slate-500 mt-1">{sub}</div>
        </div>
    );
}
